export type PolicyType = 'Individual' | 'Family Floater' | 'Senior Citizen' | 'Critical Illness' | 'Top-Up';

export type InsurerKey =
  | 'lic' |'star' |'hdfc' |'niva' |'icici' |'bajaj' |'newIndia' |'aditya' |'care';

export interface Policy {
  id: string;
  insurer: InsurerKey;
  insurerName: string;
  planName: string;
  premium: number;
  coverageAmount: number;
  policyType: PolicyType;
  claimSettlementRatio: number;
  networkHospitals: number;
  waitingPeriodYears: number;
  daycareProceduress: number;
  maternity: boolean;
  restoration: boolean;
  opd: boolean;
  criticalIllness: boolean;
  cashlessAvailable: boolean;
  taxBenefit80D: boolean;
  noClaimBonus: boolean;
  preExistingCover: boolean;
  roomRentLimit: string;
  coPayment: string;
  ambulanceCover: boolean;
  annualHealthCheckup: boolean;
  highlights: string[];
  exclusions: string[];
  buyUrl: string;
  tag?: string;
  popularityScore: number;
}

export const INSURERS: Record<InsurerKey, { name: string; color: string; bgColor: string; abbr: string }> = {
  lic: { name: 'LIC', color: 'text-blue-700', bgColor: 'bg-blue-50', abbr: 'LIC' },
  star: { name: 'Star Health', color: 'text-red-600', bgColor: 'bg-red-50', abbr: 'SH' },
  hdfc: { name: 'HDFC ERGO', color: 'text-violet-700', bgColor: 'bg-violet-50', abbr: 'HE' },
  niva: { name: 'Niva Bupa', color: 'text-teal-700', bgColor: 'bg-teal-50', abbr: 'NB' },
  icici: { name: 'ICICI Lombard', color: 'text-amber-700', bgColor: 'bg-amber-50', abbr: 'IL' },
  bajaj: { name: 'Bajaj Allianz', color: 'text-emerald-700', bgColor: 'bg-emerald-50', abbr: 'BA' },
  newIndia: { name: 'New India', color: 'text-indigo-700', bgColor: 'bg-indigo-50', abbr: 'NIA' },
  aditya: { name: 'Aditya Birla', color: 'text-pink-700', bgColor: 'bg-pink-50', abbr: 'AB' },
  care: { name: 'Care Health', color: 'text-orange-700', bgColor: 'bg-orange-50', abbr: 'CH' },
};

export const POLICIES: Policy[] = [
  {
    id: 'policy-lic-001',
    insurer: 'lic',
    insurerName: 'LIC',
    planName: 'Jeevan Arogya',
    premium: 6500,
    coverageAmount: 200000,
    policyType: 'Individual',
    claimSettlementRatio: 96.5,
    networkHospitals: 5000,
    waitingPeriodYears: 3,
    daycareProceduress: 140,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: '1% of sum insured',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      '140+ daycare procedures covered',
      'Fixed benefit plan with guaranteed payouts',
      'Government-backed LIC reliability',
      'Section 80D tax benefits up to ₹25,000',
    ],
    exclusions: [
      'Maternity and newborn expenses',
      'Cosmetic surgery and dental treatment',
      'Self-inflicted injuries',
      'War and nuclear perils',
    ],
    buyUrl: 'https://licindia.in?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-lic-001',
    tag: 'Government',
    popularityScore: 72,
  },
  {
    id: 'policy-lic-002',
    insurer: 'lic',
    insurerName: 'LIC',
    planName: 'Cancer Cover',
    premium: 4200,
    coverageAmount: 1000000,
    policyType: 'Critical Illness',
    claimSettlementRatio: 96.5,
    networkHospitals: 5000,
    waitingPeriodYears: 2,
    daycareProceduress: 0,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: true,
    cashlessAvailable: false,
    taxBenefit80D: true,
    noClaimBonus: false,
    preExistingCover: false,
    roomRentLimit: 'N/A (lump sum)',
    coPayment: 'Nil',
    ambulanceCover: false,
    annualHealthCheckup: false,
    highlights: [
      'Lump sum payout on cancer diagnosis',
      '₹10L coverage at just ₹4,200/year',
      'Covers all stages of cancer',
      'Income replacement benefit included',
    ],
    exclusions: [
      'Non-malignant tumours',
      'Pre-existing cancer conditions',
      'Skin cancer (non-melanoma)',
      'Cancer caused by HIV/AIDS',
    ],
    buyUrl: 'https://licindia.in?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-lic-002',
    tag: 'Specialised',
    popularityScore: 58,
  },
  {
    id: 'policy-star-001',
    insurer: 'star',
    insurerName: 'Star Health',
    planName: 'Family Health Optima',
    premium: 12500,
    coverageAmount: 1000000,
    policyType: 'Family Floater',
    claimSettlementRatio: 99.06,
    networkHospitals: 14000,
    waitingPeriodYears: 1,
    daycareProceduress: 600,
    maternity: true,
    restoration: true,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      '99.06% claim settlement ratio — industry best',
      '14,000+ cashless hospitals across India',
      '600+ daycare procedures covered',
      '100% sum insured restoration mid-year',
      'Newborn cover from day one',
    ],
    exclusions: [
      'Cosmetic and aesthetic treatments',
      'Experimental or unproven treatments',
      'Self-inflicted injuries',
      'Adventure sports injuries',
    ],
    buyUrl: 'https://www.starhealth.in?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-star-001',
    tag: 'Best Seller',
    popularityScore: 98,
  },
  {
    id: 'policy-star-002',
    insurer: 'star',
    insurerName: 'Star Health',
    planName: 'Senior Red Carpet',
    premium: 18000,
    coverageAmount: 500000,
    policyType: 'Senior Citizen',
    claimSettlementRatio: 99.06,
    networkHospitals: 14000,
    waitingPeriodYears: 1,
    daycareProceduress: 400,
    maternity: false,
    restoration: false,
    opd: true,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: false,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: '30% co-payment',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      'Designed for ages 60–75 years',
      'Pre-existing diseases covered from year 1',
      'OPD consultation cover included',
      'No pre-policy medical check-up required',
    ],
    exclusions: [
      'Conditions diagnosed before policy inception',
      'Dental treatment (non-accidental)',
      'Vision correction procedures',
      'Obesity and weight-loss treatments',
    ],
    buyUrl: 'https://www.starhealth.in?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-star-002',
    tag: 'Senior Specialist',
    popularityScore: 74,
  },
  {
    id: 'policy-hdfc-001',
    insurer: 'hdfc',
    insurerName: 'HDFC ERGO',
    planName: 'Optima Secure',
    premium: 10800,
    coverageAmount: 1000000,
    policyType: 'Individual',
    claimSettlementRatio: 98.0,
    networkHospitals: 13000,
    waitingPeriodYears: 2,
    daycareProceduress: 540,
    maternity: false,
    restoration: true,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      '2x coverage benefit from day one',
      '98% claim settlement ratio',
      '13,000+ network hospitals',
      'Digital-first instant claims processing',
      'Free annual health check-up',
    ],
    exclusions: [
      'Maternity expenses (not covered)',
      'Cosmetic procedures',
      'Psychiatric and mental disorders (limited)',
      'Infertility treatments',
    ],
    buyUrl: 'https://www.hdfcergo.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-hdfc-001',
    tag: 'Top Rated',
    popularityScore: 91,
  },
  {
    id: 'policy-hdfc-002',
    insurer: 'hdfc',
    insurerName: 'HDFC ERGO',
    planName: 'My:Health Suraksha',
    premium: 7200,
    coverageAmount: 500000,
    policyType: 'Individual',
    claimSettlementRatio: 98.0,
    networkHospitals: 13000,
    waitingPeriodYears: 2,
    daycareProceduress: 400,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'Single AC room',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      'Wellness benefits and fitness rewards',
      'Affordable entry-level plan at ₹7,200/yr',
      'Covers 400+ daycare procedures',
      'Digital health management app',
    ],
    exclusions: [
      'Maternity and newborn cover',
      'Cosmetic treatments',
      'Pre-existing conditions (2-year wait)',
      'Non-allopathic treatments',
    ],
    buyUrl: 'https://www.hdfcergo.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-hdfc-002',
    popularityScore: 68,
  },
  {
    id: 'policy-niva-001',
    insurer: 'niva',
    insurerName: 'Niva Bupa',
    planName: 'ReAssure 2.0',
    premium: 9500,
    coverageAmount: 1000000,
    policyType: 'Family Floater',
    claimSettlementRatio: 91.0,
    networkHospitals: 10000,
    waitingPeriodYears: 2,
    daycareProceduress: 500,
    maternity: false,
    restoration: true,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      'Unlimited restoration of sum insured',
      '10,000+ cashless hospitals',
      '500+ daycare procedures',
      'Live healthy earn rewards program',
    ],
    exclusions: [
      'Maternity and childbirth expenses',
      'Dental and vision correction',
      'Cosmetic surgery',
      'Experimental medical procedures',
    ],
    buyUrl: 'https://www.nivabupa.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-niva-001',
    tag: 'Value Pick',
    popularityScore: 82,
  },
  {
    id: 'policy-niva-002',
    insurer: 'niva',
    insurerName: 'Niva Bupa',
    planName: 'Health Companion',
    premium: 6800,
    coverageAmount: 500000,
    policyType: 'Family Floater',
    claimSettlementRatio: 91.0,
    networkHospitals: 10000,
    waitingPeriodYears: 3,
    daycareProceduress: 300,
    maternity: false,
    restoration: false,
    opd: true,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'Single standard room',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      'OPD consultation cover included',
      'Affordable family floater at ₹6,800/yr',
      'Covers 300+ daycare procedures',
      'Preventive health check-up benefits',
    ],
    exclusions: [
      'Maternity expenses',
      'Cosmetic and aesthetic surgery',
      'HIV/AIDS treatment',
      'Self-inflicted injuries',
    ],
    buyUrl: 'https://www.nivabupa.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-niva-002',
    popularityScore: 63,
  },
  {
    id: 'policy-icici-001',
    insurer: 'icici',
    insurerName: 'ICICI Lombard',
    planName: 'Complete Health Insurance',
    premium: 8500,
    coverageAmount: 1000000,
    policyType: 'Individual',
    claimSettlementRatio: 87.0,
    networkHospitals: 10000,
    waitingPeriodYears: 2,
    daycareProceduress: 450,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'Single AC room',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      '10,000+ cashless hospitals across India',
      '450+ daycare procedures covered',
      'Instant digital claims processing',
      'Free annual health check-up',
    ],
    exclusions: [
      'Maternity expenses (add-on available)',
      'Cosmetic surgery',
      'Non-allopathic treatments',
      'Obesity treatment',
    ],
    buyUrl: 'https://www.icicilombard.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-icici-001',
    popularityScore: 76,
  },
  {
    id: 'policy-icici-002',
    insurer: 'icici',
    insurerName: 'ICICI Lombard',
    planName: 'Health Advantage Plus',
    premium: 11200,
    coverageAmount: 1500000,
    policyType: 'Individual',
    claimSettlementRatio: 87.0,
    networkHospitals: 10000,
    waitingPeriodYears: 2,
    daycareProceduress: 500,
    maternity: false,
    restoration: true,
    opd: false,
    criticalIllness: true,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      '₹15L coverage with critical illness rider',
      'Sum insured restoration benefit',
      '500+ daycare procedures',
      'Global emergency cover included',
    ],
    exclusions: [
      'Maternity expenses',
      'Cosmetic procedures',
      'Pre-existing conditions (2-year wait)',
      'Non-allopathic medicine',
    ],
    buyUrl: 'https://www.icicilombard.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-icici-002',
    tag: 'High Coverage',
    popularityScore: 71,
  },
  {
    id: 'policy-bajaj-001',
    insurer: 'bajaj',
    insurerName: 'Bajaj Allianz',
    planName: 'Health Guard Gold',
    premium: 7800,
    coverageAmount: 1000000,
    policyType: 'Family Floater',
    claimSettlementRatio: 92.0,
    networkHospitals: 8500,
    waitingPeriodYears: 2,
    daycareProceduress: 480,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'Single AC room',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      '8,500+ cashless hospitals',
      '480+ daycare procedures covered',
      '92% claim settlement ratio',
      'Cumulative bonus up to 100% of sum insured',
    ],
    exclusions: [
      'Maternity expenses',
      'Cosmetic and reconstructive surgery',
      'Dental treatment (non-accidental)',
      'Experimental treatments',
    ],
    buyUrl: 'https://www.bajajallianz.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-bajaj-001',
    popularityScore: 69,
  },
  {
    id: 'policy-bajaj-002',
    insurer: 'bajaj',
    insurerName: 'Bajaj Allianz',
    planName: 'Extra Care Plus',
    premium: 5200,
    coverageAmount: 500000,
    policyType: 'Top-Up',
    claimSettlementRatio: 92.0,
    networkHospitals: 8500,
    waitingPeriodYears: 2,
    daycareProceduress: 200,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: false,
    preExistingCover: false,
    roomRentLimit: 'Shared room',
    coPayment: '20% co-payment',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      'Affordable top-up plan at ₹5,200/yr',
      'Supplements your existing base policy',
      'Simple claim process',
      'Covers hospitalisation expenses above deductible',
    ],
    exclusions: [
      'Claims below deductible amount',
      'Maternity and newborn expenses',
      'Cosmetic surgery',
      'Pre-existing conditions',
    ],
    buyUrl: 'https://www.bajajallianz.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-bajaj-002',
    tag: 'Budget',
    popularityScore: 54,
  },
  {
    id: 'policy-newIndia-001',
    insurer: 'newIndia',
    insurerName: 'New India Assurance',
    planName: 'Mediclaim Policy',
    premium: 5500,
    coverageAmount: 500000,
    policyType: 'Individual',
    claimSettlementRatio: 95.0,
    networkHospitals: 12000,
    waitingPeriodYears: 2,
    daycareProceduress: 350,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: '1% of sum insured per day',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      'Government-backed public sector insurer',
      '12,000+ network hospitals',
      '95% claim settlement ratio',
      'Trusted since 1919 — 100+ years of legacy',
    ],
    exclusions: [
      'Maternity and childbirth',
      'Cosmetic treatments',
      'Self-inflicted injuries',
      'Non-allopathic treatments',
    ],
    buyUrl: 'https://www.newindia.co.in?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-newIndia-001',
    tag: 'Government',
    popularityScore: 77,
  },
  {
    id: 'policy-newIndia-002',
    insurer: 'newIndia',
    insurerName: 'New India Assurance',
    planName: 'Senior Citizen Mediclaim',
    premium: 9800,
    coverageAmount: 500000,
    policyType: 'Senior Citizen',
    claimSettlementRatio: 95.0,
    networkHospitals: 12000,
    waitingPeriodYears: 2,
    daycareProceduress: 300,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: false,
    preExistingCover: true,
    roomRentLimit: '1% of sum insured per day',
    coPayment: '20% co-payment',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      'For ages 60–80 years',
      'Pre-existing diseases covered after 18 months',
      'Government insurer — maximum trust',
      'Covers 300+ daycare procedures',
    ],
    exclusions: [
      'Dental treatment (non-accidental)',
      'Cosmetic surgery',
      'War and nuclear perils',
      'Experimental treatments',
    ],
    buyUrl: 'https://www.newindia.co.in?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-newIndia-002',
    tag: 'Senior Specialist',
    popularityScore: 65,
  },
  {
    id: 'policy-aditya-001',
    insurer: 'aditya',
    insurerName: 'Aditya Birla Health',
    planName: 'Activ Health Platinum Enhanced',
    premium: 10200,
    coverageAmount: 1000000,
    policyType: 'Individual',
    claimSettlementRatio: 94.0,
    networkHospitals: 9000,
    waitingPeriodYears: 2,
    daycareProceduress: 520,
    maternity: false,
    restoration: true,
    opd: true,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      'Wellness rewards — earn up to 30% premium back',
      '9,000+ cashless hospitals',
      'OPD doctor consultations covered',
      'Chronic management program included',
      '94% claim settlement ratio',
    ],
    exclusions: [
      'Maternity expenses',
      'Cosmetic and aesthetic surgery',
      'Infertility treatments',
      'Experimental procedures',
    ],
    buyUrl: 'https://www.adityabirlacapital.com/health-insurance?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-aditya-001',
    tag: 'Wellness Rewards',
    popularityScore: 85,
  },
  {
    id: 'policy-aditya-002',
    insurer: 'aditya',
    insurerName: 'Aditya Birla Health',
    planName: 'Activ Fit',
    premium: 6500,
    coverageAmount: 500000,
    policyType: 'Individual',
    claimSettlementRatio: 94.0,
    networkHospitals: 9000,
    waitingPeriodYears: 2,
    daycareProceduress: 350,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: false,
    roomRentLimit: 'Single standard room',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      'Designed for young adults 18–35 years',
      'Fitness tracking and health rewards',
      'Affordable at ₹6,500/year',
      'Digital-first claims experience',
    ],
    exclusions: [
      'Pre-existing conditions (2-year wait)',
      'Maternity expenses',
      'Cosmetic surgery',
      'Non-allopathic treatments',
    ],
    buyUrl: 'https://www.adityabirlacapital.com/health-insurance?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-aditya-002',
    tag: 'Youth Plan',
    popularityScore: 66,
  },
  {
    id: 'policy-care-001',
    insurer: 'care',
    insurerName: 'Care Health',
    planName: 'Care Supreme',
    premium: 11500,
    coverageAmount: 1000000,
    policyType: 'Family Floater',
    claimSettlementRatio: 90.0,
    networkHospitals: 19000,
    waitingPeriodYears: 1,
    daycareProceduress: 550,
    maternity: false,
    restoration: true,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'No limit',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: true,
    highlights: [
      'Largest hospital network — 19,000+ hospitals',
      'Unlimited sum insured restoration',
      '550+ daycare procedures covered',
      '1-year waiting period for pre-existing conditions',
    ],
    exclusions: [
      'Maternity expenses (add-on available)',
      'Cosmetic treatments',
      'Dental care (non-accidental)',
      'Experimental treatments',
    ],
    buyUrl: 'https://www.careinsurance.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-care-001',
    tag: 'Largest Network',
    popularityScore: 88,
  },
  {
    id: 'policy-care-002',
    insurer: 'care',
    insurerName: 'Care Health',
    planName: 'Care Classic',
    premium: 7200,
    coverageAmount: 500000,
    policyType: 'Individual',
    claimSettlementRatio: 90.0,
    networkHospitals: 19000,
    waitingPeriodYears: 2,
    daycareProceduress: 400,
    maternity: false,
    restoration: false,
    opd: false,
    criticalIllness: false,
    cashlessAvailable: true,
    taxBenefit80D: true,
    noClaimBonus: true,
    preExistingCover: true,
    roomRentLimit: 'Single standard room',
    coPayment: 'Nil',
    ambulanceCover: true,
    annualHealthCheckup: false,
    highlights: [
      'Access to 19,000+ cashless hospitals',
      'Comprehensive basic coverage at ₹7,200/yr',
      '400+ daycare procedures covered',
      'Simple and transparent policy terms',
    ],
    exclusions: [
      'Maternity and newborn expenses',
      'Cosmetic surgery',
      'Self-inflicted injuries',
      'Non-allopathic treatments',
    ],
    buyUrl: 'https://www.careinsurance.com?utm_source=medicompare&utm_medium=affiliate&utm_campaign=health_insurance&utm_content=policy-care-002',
    popularityScore: 60,
  },
];

export function formatCurrency(amount: number): string {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(0)} L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount}`;
}

export function formatPremium(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getCSRBadgeClass(csr: number): string {
  if (csr >= 95) return 'badge-csr-high';
  if (csr >= 88) return 'badge-csr-mid';
  return 'badge-csr-low';
}

export function getCSRLabel(csr: number): string {
  if (csr >= 95) return 'Excellent';
  if (csr >= 88) return 'Good';
  return 'Average';
}